/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { DesktopApp } from './types';
import { normalizeDesktopProjectId } from './utils';

const PROJECT_SCOPED_APP_IDS = new Set(['land-scoring', 'simulate', 'create-parcels']);
/** Project-scoped apps whose window picks a project itself instead of opening the list. */
const PROJECT_PICKER_APP_IDS = new Set(['land-scoring', 'create-parcels', 'simulate']);

/** Apps whose default location depends on the selected desktop project. */
export const isProjectScopedApp = (app: Pick<DesktopApp, 'id'>) =>
  PROJECT_SCOPED_APP_IDS.has(app.id);

/**
 * Location a window renders. A window still at its app's default path follows the selected
 * project; once navigated elsewhere it keeps its own location.
 */
export const resolveDesktopWindowPath = ({
  app,
  path,
  selectedProjectId,
}: {
  app: DesktopApp;
  path: string;
  selectedProjectId: string | null;
}) => (path === app.defaultPath ? resolveDesktopAppPath({ app, selectedProjectId }) : path);

export const resolveDesktopAppPath = ({
  app,
  selectedProjectId,
}: {
  app: DesktopApp;
  selectedProjectId: string | null;
}) => {
  const projectId = normalizeDesktopProjectId(selectedProjectId);

  if (!projectId) {
    // Land Scoring, Create Parcels and Project Simulate ask for a project in their own window; the
    // others open the project list.
    if (isProjectScopedApp(app) && !PROJECT_PICKER_APP_IDS.has(app.id)) {
      return '/projects';
    }
    return app.defaultPath;
  }

  switch (app.id) {
    case 'land-scoring':
      return `/projects/${projectId}/land-scoring`;
    case 'simulate':
      return `/projects/${projectId}/simulate`;
    case 'create-parcels':
      return `/projects/${projectId}/create-parcels`;
    default:
      return app.defaultPath;
  }
};
